import { FC, useMemo, useState } from 'react';
import { Box, HStack, useColorModeValue, Spinner, Center, Text } from '@chakra-ui/react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { SalesFunnel, LeadWithFunnel } from '../../types';
import { FunnelStepCard } from './FunnelStepCard';
import { LeadCard } from './LeadCard';

export interface KanbanBoardProps {
  funnel: SalesFunnel;
  leads: LeadWithFunnel[];
  loading?: boolean;
  onLeadClick?: (lead: LeadWithFunnel) => void;
  onLeadMove?: (leadId: number, fromStepId: string, toStepId: string) => Promise<void>;
}

export const KanbanBoard: FC<KanbanBoardProps> = ({
  funnel,
  leads,
  loading = false,
  onLeadClick,
  onLeadMove,
}) => {
  const bgColor = useColorModeValue('gray.100', 'gray.800');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isMoving, setIsMoving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group leads by step
  const leadsByStep = useMemo(() => {
    const grouped: Record<string, LeadWithFunnel[]> = {};
    
    // Initialize all steps
    funnel.steps.forEach(step => {
      grouped[step.id] = [];
    });

    // Group leads by their current step
    leads.forEach(lead => {
      if (lead.current_step_id && grouped[lead.current_step_id]) {
        grouped[lead.current_step_id].push(lead);
      } else if (funnel.steps.length > 0) {
        // If no step assigned, put in first step
        grouped[funnel.steps[0].id].push(lead);
      }
    });

    return grouped;
  }, [funnel.steps, leads]);

  // Sort steps by order
  const sortedSteps = useMemo(() => {
    return [...funnel.steps].sort((a, b) => a.order - b.order);
  }, [funnel.steps]);

  const stepColorMap = useMemo(() => {
    const map = new Map<string, string | undefined>();
    funnel.steps.forEach(step => {
      map.set(step.id, step.color);
    });
    return map;
  }, [funnel.steps]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // This allows us to show visual feedback during drag
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || !active) return;

    const leadId = parseInt(active.id as string);
    const toStepId = over.id as string;

    // Find the lead and its current step
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    const fromStepId = lead.current_step_id || sortedSteps[0]?.id;
    if (!fromStepId || fromStepId === toStepId) return;

    // Only move if it's a different step
    if (fromStepId !== toStepId && onLeadMove) {
      setIsMoving(true);
      try {
        await onLeadMove(leadId, fromStepId, toStepId);
      } catch (error) {
        console.error('Failed to move lead:', error);
      } finally {
        setIsMoving(false);
      }
    }
  };

  const activeLead = useMemo(() => {
    if (!activeId) return null;
    const leadId = parseInt(activeId);
    return leads.find(l => l.id === leadId) || null;
  }, [activeId, leads]);

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box w="100%" p={4} bg={bgColor} borderRadius="lg" overflowX="auto" position="relative">
        {isMoving && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.200"
            zIndex={1000}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="lg"
          >
            <Spinner size="lg" />
          </Box>
        )}
        <HStack align="start" spacing={4} minH="500px">
          {sortedSteps.map((step) => (
            <FunnelStepCard
              key={step.id}
              step={step}
              leads={leadsByStep[step.id] || []}
              onLeadClick={onLeadClick}
              stepId={step.id}
            />
          ))}
        </HStack>
        {sortedSteps.length === 0 && (
          <Center h="200px">
            <Text color={useColorModeValue('gray.500', 'gray.400')}>
              No funnel steps configured
            </Text>
          </Center>
        )}
      </Box>
      <DragOverlay>
        {activeLead ? (
          <Box maxW="280px">
            <LeadCard
              lead={activeLead}
              variant="compact"
              accentColor={
                activeLead.current_step_id
                  ? stepColorMap.get(activeLead.current_step_id)
                  : undefined
              }
              campaignLabel={activeLead.campaign_link_id ? `#${activeLead.campaign_link_id}` : undefined}
              showCampaign
            />
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
